import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  popular?: boolean;
}

export default function PlanSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const email = searchParams.get('email');

  const plans: Plan[] = [
    {
      id: 'trial',
      name: 'Plan Prueba',
      price: 0,
      period: '30 días gratis',
      features: [
        'Durante el periodo de prueba, obtendrás acceso a todas las funciones sin limitaciones, incluyendo:',
        'Creación de campañas de donación',
        'Publicación de casos de adopción',
        'Panel de administración completo',
        '¡Comienza hoy y sé parte del cambio que ellos necesitan! 🐾💛'
      ]
    },
    {
      id: 'premium',
      name: 'Plan Premium',
      price: 299,
      period: 'por mes',
      popular: true,
      features: [
        'Campañas de donación ilimitadas',
        'Casos de adopción ilimitados',
        'Panel de administración completo',
        'Integración con redes sociales',
        'Herramientas de marketing',
        'Soporte prioritario'
      ]
    }
  ];

  const handlePlanSelect = async () => {
    if (!selectedPlan) {
      alert('Por favor selecciona un plan');
      return;
    }

    setLoading(true);

    try {
      // Get user data
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error || !userData) {
        alert('Usuario no encontrado');
        return;
      }

      // Update user plan
      const { error: updateError } = await supabase
        .from('users')
        .update({
          plan: selectedPlan,
          plan_status: selectedPlan === 'trial' ? 'trial' : 'incomplete',
          trial_end_date: selectedPlan === 'trial' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : null
        })
        .eq('id', userData.id);

      if (updateError) {
        alert('Error al actualizar el plan');
        return;
      }

      // Redirect to Stripe based on plan
      if (selectedPlan === 'trial') {
        // For trial, redirect to admin panel directly
        const sessionData = {
          id: userData.id,
          email: userData.email,
          plan: selectedPlan,
          plan_status: 'trial',
          subscription_status: 'inactive',
          trial_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          shelter_name: userData.shelter_name,
          city: userData.city,
          state: userData.state,
          phone: userData.phone,
          loginTime: Date.now(),
          expiresAt: Date.now() + (3 * 60 * 60 * 1000)
        };
        
        localStorage.setItem('user_session', JSON.stringify(sessionData));
        navigate('/admin');
      } else if (selectedPlan === 'premium') {
        // For premium, redirect to Stripe
        const response = await fetch('https://huellitas-unidas-server.vercel.app/api/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: userData.id,
            customer_email: userData.email,
            plan: 'premium'
          }),
        });

        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert('Error al crear la sesión de pago');
        }
      }

    } catch (error) {
      console.error('Error:', error);
      alert('Error al procesar la selección');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-green-600 text-2xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              ¡Email Confirmado!
            </h1>
            <p className="text-lg text-gray-600">
              Ahora selecciona el plan que mejor se adapte a tus necesidades
            </p>
          </div>
        </div>
      </div>

      {/* Plans Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid gap-8 md:grid-cols-2">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`border rounded-xl p-8 hover:border-gray-300 transition-colors bg-white flex flex-col h-full relative cursor-pointer ${
                selectedPlan === plan.id
                  ? 'border-orange-500 transform scale-105'
                  : plan.id === 'trial' ? 'border-blue-200' : 'border-orange-200'
              } ${plan.popular ? 'ring-2 ring-orange-500' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Recomendado
                  </span>
                </div>
              )}
              
              <div className="text-center mb-8">
                <div className={`w-12 h-12 ${plan.id === 'trial' ? 'bg-blue-100' : 'bg-orange-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <svg className={`w-6 h-6 ${plan.id === 'trial' ? 'text-blue-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {plan.id === 'trial' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    )}
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-4">
                  {plan.id === 'trial' 
                    ? 'Acceso completo por 30 días gratis'
                    : 'Acceso completo y funciones avanzadas'
                  }
                </p>
                <div className="text-2xl font-bold text-gray-900">
                  {plan.price === 0 ? 'Gratis' : `$${plan.price}`}
                </div>
                <div className="text-sm text-gray-500">{plan.period}</div>
              </div>

              <div className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-center mt-auto">
                <button
                  onClick={handlePlanSelect}
                  disabled={loading || selectedPlan !== plan.id}
                  className={`w-full py-3 px-6 rounded-lg transition-colors font-medium ${
                    selectedPlan === plan.id
                      ? 'bg-orange-500 hover:bg-orange-600 text-white'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </>
                  ) : selectedPlan === plan.id ? (
                    <>
                      <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      {plan.id === 'trial' ? 'Comenzar Prueba Gratis' : 'Actualizar a Premium'}
                    </>
                  ) : (
                    'Selecciona este plan'
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-3">
                  {plan.id === 'premium' ? 'Pago seguro procesado por Stripe' : 'Sin compromiso'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="font-medium text-gray-900">Información Importante</h4>
            </div>
            <p className="text-gray-600 text-sm">
              Selecciona el plan que mejor se adapte a tus necesidades. Puedes cambiar o cancelar tu suscripción en cualquier momento desde tu panel de administración.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
